/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable arrow-body-style */

import React, { useState, useEffect } from "react";
import clsx from "clsx";
import moment from "moment";
import PropTypes from "prop-types";
import {
  CustomDatePicker,
  CustomSelect,
  EllipsedText,
  ValidatingTextField,
} from "../FormComponents";
import { API } from "../../apis/api";
import {
  API_RESOURCE_URLS,
  DATE_FORMAT,
  DISPLAY_MESSAGES,
} from "../../constants";
import { isPositiveInteger, isQuantityValid } from "../../utils/utils";
import { reasons, agencies } from "../../staticData";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";
import { useErrorHandler } from "react-error-boundary";
import { buildErrorMessage } from "../../apis/calls";
import { Modal, Box } from "@material-ui/core";
import styles from "./AddEmergencyAsnFormMes4.module.css";

const AddEmergencyAsnFormMes4 = ({
  title,
  open,
  handleClose,
  plant,
  plantName,
  line,
  lineName,
  lineBackgroundColor,
  lastAsnGeneratedDate,
  updatePlanRows,
  skipToLastPage,
  cancelButtonText,
  submitButtonText,
}) => {
  const [vcs, setVcs] = useState({ loading: false, data: [] });
  const [highlightMandatoryFields, setHighlightMandatoryFields] = useState(
    false
  );
  const {
    showInternalError,
    showLoading,
    showError,
    showSuccess,
  } = usePopupManager();
  const handleError = useErrorHandler();
  const [reason, setReason] = useState("");
  const [agency, setAgency] = useState("");
  const [
    displayVcCombinationNotUniqueError,
    setDisplayVcCombinationNotUniqueError,
  ] = useState(false);
  const [vc, setVc] = useState("");
  const [comment, setComment] = useState("");
  const [additionalQuantity, seAdditionalQuantity] = useState("");
  const [planDate, setPlanData] = useState(null);
  const [firstEmergencyAsnDate, setFirstEmergencyAsnDate] = useState(null);
  const [lastAsn, setLastAsn] = useState(null);
  const isLucknowProlifePlant = plantName === 'Lucknow-Prolife'
  const parentName = isLucknowProlifePlant ? 'Part' : 'VC'

  const loadFirstEmergencyAsnDate = async () => {
    try {
      const response = await API.get(
        API_RESOURCE_URLS.FIRST_EMERGENCY_ASN_DATE,
        {
          params: {
            plant,
            line,
          },
        },
      );
      const data = response.data;
      const lineData = data.filter(entry => entry.line === line)[0];
      setFirstEmergencyAsnDate(moment(lineData.lastDate, DATE_FORMAT.ISO));
    } catch (error) {
      console.warn(
        'failed to get fist-possible-emergency-asn-date' +
          buildErrorMessage(error),
      );
      setFirstEmergencyAsnDate(null);
    }
  };

  const loadLastAsnForLine = async (lastAsnDate, selectedLine) => {
    if (!lastAsnDate) return setLastAsn(null);
    const dateString = lastAsnDate.format(DATE_FORMAT.ISO);
    try {
      const response = await API.get(API_RESOURCE_URLS.VC_ASN, {
        params: {
          from: dateString,
          to: dateString,
          line: selectedLine,
          plant,
        },
      });
      const asns = response.data;
      const lastSerialNumber = Math.max(...asns.map(asn => asn.serialNumber));
      const asnWithLastSerialNumber = asns.find(
        asn => asn.serialNumber === lastSerialNumber,
      );
      const lastAsnString = asnWithLastSerialNumber
        ? asnWithLastSerialNumber.asn
        : null;
      setLastAsn(lastAsnString);      
    } catch (error) {
      console.error(
        'failed to get last asn on add emergency asn, err: ',
        buildErrorMessage(error),
      );
      showInternalError();
    }
  };

  const checkForMandatoryFieldsAndGetSelectedVc = () => {
    const vcCombination = getSelectedVcCombination();    
    return vcCombination.vc;
  };

  const doesVcHaveCostReleaseForDate = async (vc, dateString) => {    
    try {
      const response = await API.get(API_RESOURCE_URLS.VC_COST_RELEASE, {
        params: {
          plannedDate: dateString,
          material: vc,
          plant,
        },
      });
      const VcConstRelease = response.data.released.includes(vc);
      return VcConstRelease;
    } catch (error) {
      console.error(buildErrorMessage(error));
      showInternalError();
      return false;
    }
  };

  const formatOptionsForSelect = (...options) =>
    options.map((option) => ({ value: option, label: option }));

  const getVcOptions = () =>
    formatOptionsForSelect(...vcs.data.map(({ vc }) => vc));

  const getDescriptionForVc = (vcData) => {
    const selectedVc = vcData && vcData.value;
    const selectedVcData = vcs.data.filter(({ vc }) => vc === selectedVc)[0];
    return selectedVcData ? selectedVcData.description : <strong>--</strong>;
  };

  const reasonOptions = formatOptionsForSelect(...reasons);
  const agencyOptions = formatOptionsForSelect(...agencies);

  const handleSelectionCurry = (setState) => (valueObject) =>
    setState(valueObject);

  const loadData = async (setState, url, params) => {
    try {
      const response = await API.get(url, {
        params: params,
      });
      setState({ loading: false, data: response.data });
    } catch (error) {
      console.error(buildErrorMessage(error));
      handleError(error);
    }
  };

  const loadVcs = async () => {
    return loadData(setVcs, API_RESOURCE_URLS.VC_DESCRIPTION, {
      line,
      plant,
    });
  };

  const getSelectedVcCombination = () => ({   
    vc: vc && vc.value,    
  });

  const runPreSubmissionChecks = () => {
    if (
      vc === null ||
      planDate === null ||
      additionalQuantity === null ||
      reason === null ||
      agency === null
    ) {
      setHighlightMandatoryFields(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    const vcCombination = getSelectedVcCombination();    
    try {
      await API.patch(API_RESOURCE_URLS.VC_QUANTITY_SEQUENCE_ASN, {
        plant,
        line,
        ...vcCombination,
        planDate: planDate,
        additionalQuantity: Number(additionalQuantity),
        reason: reason.value,
        agency: agency.value,
        comment,
      });      
      handleClose();
      showSuccess({
        contextText: `${isLucknowProlifePlant?DISPLAY_MESSAGES.ASN_ADD_SUCCESS_PART:DISPLAY_MESSAGES.ASN_ADD_SUCCESS} ${moment(
          planDate,
          DATE_FORMAT.ISO,
        ).format('DD-MMM-YY')}`,
        info: `${parentName} ${vcCombination.vc}`,
      });
      await updatePlanRows();
      skipToLastPage();
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: DISPLAY_MESSAGES.ASN_ADD_FAILURE,
        info: '',
        error: error,
      });
    }
  };

  const resetState = async () => {
    setVc(null);
    setPlanData(null);
    setReason(null);
    setAgency(null);
    setComment(null);
    seAdditionalQuantity(null);
    setDisplayVcCombinationNotUniqueError(null);
    setHighlightMandatoryFields(null);
    setFirstEmergencyAsnDate(null);
    setLastAsn(null);
  };

  useEffect(() => {
    if (open) {
      loadVcs();
      loadFirstEmergencyAsnDate();
      loadLastAsnForLine(lastAsnGeneratedDate,line);
    } else {
      resetState();
    }
  }, [open, plant, line, lastAsnGeneratedDate]);

  return (
    <>
      <Modal open={open} onClose={handleClose} disableBackdropClick>
        <div className={styles.formContainer}>
          <div className={styles.formTitle}>
            <div>
              <span style={{ backgroundColor: "#EAEAEA" }}>{title}</span>
            </div>
            <div
              className={styles.formlLineDiv}
              style={{
                backgroundColor: lineBackgroundColor,
              }}
            />
          </div>
          <div className={styles.subHeading}>
            <div className={styles.formSec}>
              <p className={styles.note}>
                Note: These ASN(s) will be added at the end of the Lineup for a
                selected Plan date.
                <span className={styles.optionalFields}>
                  ** Optional Fields
                </span>
              </p>
            </div>
          </div>
          <Box className={styles.box_Line}>
            <div className={styles.gridcontainer_Basic}>
              <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                <label className={styles.label}>Plant</label>
              </div>
              <div className={styles.lineModalLeftInput_BasicInfo}>
                <input
                  aria-label="Plant"
                  className={styles.textfield}
                  value={plantName}
                  disabled
                />
              </div>
              <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                <label className={styles.label}>Line</label>
              </div>
              <div className={styles.lineModalLeftInput_BasicInfo}>
                <input
                  aria-label="Line"
                  className={styles.textfield}
                  value={lineName}
                  disabled
                />
              </div>
            </div>
          </Box>
          <div className={styles.BasicInfo_formSec}>
            <span class={styles.BasicInfo_rowTitle}>Basic Info</span>
            <Box className={styles.box_BasicInfo}>
              <div className={styles.gridcontainer_VCDetails}>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.label}>{parentName} Number</label>
                </div>
                <div className={styles.lineModalLeftCustomSelect_BasicInfo}>
                  <CustomSelect
                    name="vc"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    showError={displayVcCombinationNotUniqueError}
                    options={getVcOptions(vcs.data)}
                    className={clsx(styles.select, styles.sel1)}
                    value={vc}
                    onChange={handleSelectionCurry(setVc)}
                    maxMenuHeight={300}
                  />
                </div>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.label}>{parentName} Description</label>
                </div>
                <div className={styles.lineModalLeftLabel_VCDescription}>
                  <EllipsedText
                    value={getDescriptionForVc(vc, vcs.data)}
                    className={styles.description}
                  />
                </div>
              </div>
            </Box>
            <span class={styles.BasicInfo_rowTitle}>Plan details</span>
            <Box className={styles.box_PlanDetails}>
              <div className={styles.gridcontainer_VCDetails}>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.label}>Start Date</label>
                </div>
                <div className={styles.lineModalCustomDatePicker}>
                <CustomDatePicker
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}                    
                    autoOk
                    inputVariant="outlined"
                    value={planDate}
                    onChange={date => {
                      setPlanData(date.format(DATE_FORMAT.ISO));
                    }}
                    inputProps={{
                      className: clsx(styles.select, styles.dateInput),
                    }}
                    emptyLabel="dd/mm/yyyy"
                    minDate={
                      process.env.NODE_ENV === 'test'
                        ? moment(1)
                        : firstEmergencyAsnDate || moment()
                    }
                    maxDate={
                      process.env.NODE_ENV === 'test'
                        ? moment('3020', 'YYYY')
                        : lastAsnGeneratedDate || moment()
                    }
                    format="DD/MM/yyyy"
                    data-testid="date-picker-div"
                  />
                </div>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.label}>Additional Qty.</label>
                </div>
                <div className={styles.lineModalLeftInput_BasicInfo}>
                  <ValidatingTextField
                    isMandatory
                    validationFn={isPositiveInteger}
                    markIfEmpty={highlightMandatoryFields}
                    isQuantityValid={isQuantityValid(additionalQuantity)}
                    aria-label="additional-quantity-input"
                    validationHelperText={
                      DISPLAY_MESSAGES.POSITIVE_NUMBER_VALIDATION_WARNING
                    }
                    quantityValidationText={DISPLAY_MESSAGES.INVALID_INPUT}
                    variant="outlined"
                    value={additionalQuantity}
                    onChange={(e) => seAdditionalQuantity(e.target.value)}
                    placeholder="(Enter Target)"
                    className={styles.textField}
                    inputProps={{
                      className: styles.textInput,
                      'data-testid': 'additional-quantity-input',
                    }}
                    FormHelperTextProps={{
                      className: styles.helperText,
                    }}
                  />
                </div>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.label}>Planned for</label>
                </div>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.labelPlanDetails}>{lastAsnGeneratedDate
                      ? lastAsnGeneratedDate.format('DD-MM-YYYY')
                      : '--'}</label>
                </div>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.label}>ASN</label>
                </div>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.labelPlanDetails}>{lastAsn || '--'}</label>
                </div>
              </div>
            </Box>
            <span class={styles.BasicInfo_rowTitle}>Revision details</span>
            <Box className={styles.box_Revision}>
              <div className={styles.gridcontainer_VCDetails}>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.label}>Reason</label>
                </div>
                <div className={styles.lineModalLeftCustomSelect_BasicInfo}>
                  <CustomSelect
                    name="reason"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    options={reasonOptions}
                    className={clsx(styles.select, styles.sel1)}
                    value={reason}
                    onChange={handleSelectionCurry(setReason)}
                  />
                </div>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.label}>Agency</label>
                </div>
                <div className={styles.lineModalLeftCustomSelect_BasicInfo}>
                  <CustomSelect
                    name="agency"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    options={agencyOptions}
                    className={clsx(styles.select, styles.sel1)}
                    value={agency}
                    onChange={handleSelectionCurry(setAgency)}
                  />
                </div>
                <div className={styles.plantDetailsLeftLabel_BasicInfo}>
                  <label className={styles.label}>Comments **</label>
                </div>
                <div className={styles.lineModalLeftComment_BasicInfo}>
                  <textarea
                    className={styles.comments}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  ></textarea>
                </div>
              </div>
            </Box>
          </div>

          <div className={styles.bottomRow}>
            <button
              type="button"
              className={styles.cancelBt}
              onClick={handleClose}
            >
              {cancelButtonText}
            </button>
            <span>
              <button
                type="button"
                onClick={(e) => {
                  if (e.detail > 1) {
                    return;
                  }
                  const vc = checkForMandatoryFieldsAndGetSelectedVc();
                  if(runPreSubmissionChecks())
                  {
                    showLoading();
                    return doesVcHaveCostReleaseForDate(vc, planDate).then(
                      hasCostRelease => {
                        if (hasCostRelease) {
                          handleSubmit();
                        } else
                          showError({
                            contextText:
                              DISPLAY_MESSAGES.VC_COST_RELEASE_NOT_FOUND_FOR_SEQUENCE,
                            info: vc,
                          });
                        return false;
                      },
                    );
                  }
                  return false;
                }}
                className={styles.submitBt}
              >
                {submitButtonText}
              </button>
            </span>
          </div>
        </div>
      </Modal>
    </>
  );
};

AddEmergencyAsnFormMes4.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  shopName: PropTypes.string,
  title: PropTypes.string.isRequired,
  lastAsnGeneratedDate: PropTypes.objectOf(moment),
  lineBackgroundColor: PropTypes.string.isRequired,
  updatePlanRows: PropTypes.func.isRequired,
  skipToLastPage: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  cancelButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
};

export default AddEmergencyAsnFormMes4;
